import { alpha, Avatar, Badge, Card, CardActions, CardContent, CardHeader, CircularProgress, Divider, IconButton, InputBase, makeStyles, Paper, SvgIcon, Theme, Typography } from "@material-ui/core";
import { blue, deepOrange, green, grey, orange, red, teal } from "@material-ui/core/colors";
import ClearOutlinedIcon from '@material-ui/icons/ClearOutlined';
import { default as DescriptionOutlinedIcon, default as InvoiceIcon } from '@material-ui/icons/DescriptionOutlined';
import FactDateIcon from '@material-ui/icons/EventAvailableOutlined';
import PlanDateIcon from '@material-ui/icons/EventOutlined';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import LocationDividerIcon from '@material-ui/icons/ForwardOutlined';
import CargoIcon from '@material-ui/icons/LocalMallOutlined';
import DeliveryIcon from '@material-ui/icons/LocalShippingOutlined';
import NotificationsActiveOutlinedIcon from '@material-ui/icons/NotificationsActiveOutlined';
import PaymentIcon from '@material-ui/icons/PaymentOutlined';
import PartnerIcon from '@material-ui/icons/PersonPinCircleOutlined';
import PdfIcon from '@material-ui/icons/PictureAsPdfOutlined';
import LocationIcon from '@material-ui/icons/RoomOutlined';
import EventIcon from '@material-ui/icons/ScheduleOutlined';
import SearchIcon from '@material-ui/icons/Search';
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import * as Tone from "tone";
import { ListOrderItem, ListOrdersQuery, OrderLocation, useListOrdersQuery, useLogoutMutation, useSaveTokenMutation } from "../../graphql/generated/graphql";
import BarcodeDialog from "../../shared/BarcodeDialog";
import useInput from "../../shared/Hooks/useInput";
import useUrlQuery from "../../shared/Hooks/useUrlQuery";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(2),
    paddingTop: 54 + theme.spacing(1),
    position: 'relative',
  },
  topBar: {
    position: 'fixed',
    top: 48,
    height: 48,
    left: 0,
    right: 0,
    zIndex: 1200,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
    backgroundColor: theme.palette.type === 'light' ? blue[500] : '#35363a',
    color: theme.palette.type === 'light' ? theme.palette.common.white : grey[300],
    // borderBottom: `1px solid ${theme.palette.divider}`,
    boxShadow: '0px 3px 3px 0px rgba(0,0,0,0.2)',
  },
  '@global': {
    '#root header': {
      boxShadow: 'none !important',
    },
  },
  topBarHidden: {
    transform: 'translateY(-100%)',
    transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
  },
  filters: {
    width: 68,
  },
  readBarcodeButton: {
    padding: 2,
    borderWidth: 1,
    borderColor: theme.palette.divider,
    borderStyle: 'solid',
    borderRadius: 4,
    // backgroundColor: theme.palette.background.paper,
    // boxShadow: theme.shadows[1],
    color: 'inherit',
    marginRight: theme.spacing(0.5),
  },
  barcodeIcon: {
    width: 26,
    height: 26,
  },
  expandAllToggleButton: {
    padding: 6,
    borderWidth: 1,
    borderColor: theme.palette.divider,
    borderStyle: 'solid',
    borderRadius: 4,
    // backgroundColor: theme.palette.background.paper,
    // boxShadow: theme.shadows[1],
    color: 'inherit',
  },
  searchBox: {
    padding: '0px 0px',
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1),
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    boxShadow: 'unset',
    border: 0,
  },
  searchInput: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    '@global': {
      'input[type=search]::-ms-reveal': { display: 'none', width: 0, height: 0, },
      'input[type=search]::-ms-clear': { display: 'none', width: 0, height: 0, },
      'input[type="search"]::-webkit-search-decoration, input[type="search"]::-webkit-search-cancel-button, input[type="search"]::-webkit-search-results-button, input[type="search"]::-webkit-search-results-decoration': { display: 'none', },
    },
    color: theme.palette.getContrastText(theme.palette.primary.main),
  },
  divider: {
    height: 25,
  },
  searchButton: {
    padding: 4,
    color: theme.palette.getContrastText(theme.palette.primary.main),
  },
  pinterestColumns: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  pinterestColumn: {
    flex: '1 1 auto',
    marginRight: theme.spacing(1),
    '&:last-child': {
      marginRight: 0,
    },
  },
  pinterestColumnsSingle: {
  },
  pinterestColumnSingle: {
  },
  cardExpanded: {
    marginBottom: theme.spacing(1),
    transition: 'margin-top 0.3s cubic-bezier(0.4,0,0.2,1), margin-bottom 0.3s cubic-bezier(0.4,0,0.2,1)',
  },
  cardExpandedTopMargin: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    transition: 'margin-top 0.3s cubic-bezier(0.4,0,0.2,1), margin-bottom 0.3s cubic-bezier(0.4,0,0.2,1)',
  },
  cardCollapsed: {
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRadius: 0,
    border: "unset",
    minHeight: 81,
    transition: 'margin-top 0.3s cubic-bezier(0.4,0,0.2,1), margin-bottom 0.3s cubic-bezier(0.4,0,0.2,1)',
  },
  cardHeaderExpanded: {
    transition: 'padding 0.3s cubic-bezier(0.4,0,0.2,1)',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
  cardHeaderCollapsed: {
    transition: 'padding 0.3s cubic-bezier(0.4,0,0.2,1)',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  cardStatusGroup: {
    display: 'flex',
    alignItems: 'center',
  },
  cardActionButtonExpanded: {
    marginTop: -8,
    marginBottom: -8,
    marginRight: -8,
    marginLeft: -8,
    width: 40,
    height: 40,
    transition: 'margin-top 0.3s cubic-bezier(0.4,0,0.2,1)',
  },
  cardActionButtonCollapsed: {
    marginTop: 0,
    marginBottom: -8,
    marginRight: -8,
    marginLeft: -8,
    width: 40,
    height: 40,
    transition: 'margin-top 0.3s cubic-bezier(0.4,0,0.2,1)',
  },
  newOrderBadge: {
    '& .MuiBadge-badge': {
      backgroundColor: orange[600],
      color: theme.palette.getContrastText(orange[600]),
    },
  },
  selectedOrderBadge: {
    '& .MuiBadge-badge': {
      backgroundColor: red[700],
      color: theme.palette.getContrastText(red[700]),
      boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
      width: 24,
      height: 24,
      minWidth: 24,
      minHeight: 24,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 18,
      padding: 0,
    },
  },
  newOrderEventBadge: {
    '& .MuiBadge-badge': {
      backgroundColor: orange[600],
      color: theme.palette.getContrastText(orange[600]),
    },
  },
  selectedOrderEventBadge: {
    '& .MuiBadge-badge': {
      backgroundColor: red[700],
      color: theme.palette.getContrastText(red[700]),
    },
  },
  orderAvatar: {
    backgroundColor: grey[600],
    color: theme.palette.getContrastText(grey[600]),
  },
  orderAvatarInProgress: {
    backgroundColor: blue[600],
    color: theme.palette.getContrastText(blue[600]),
  },
  orderAvatarCompleted: {
    backgroundColor: green[600],
    color: theme.palette.getContrastText(green[600]),
  },
  orderAvatarError: {
    backgroundColor: red[600],
    color: theme.palette.getContrastText(red[600]),
  },
  statusAvatar: {
    width: theme.spacing(3),
    height: theme.spacing(3),
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
    marginRight: 2,
    borderWidth: 0,
  },
  completedAvatar: {
    width: theme.spacing(3),
    height: theme.spacing(3),
    backgroundColor: green[600],
    color: theme.palette.getContrastText(green[600]),
    marginRight: 2,
    borderWidth: 0,
  },
  inProgressAvatar: {
    width: theme.spacing(3),
    height: theme.spacing(3),
    backgroundColor: blue[600],
    color: theme.palette.getContrastText(blue[600]),
    marginRight: 2,
    borderWidth: 0,
  },
  errorAvatar: {
    width: theme.spacing(3),
    height: theme.spacing(3),
    backgroundColor: red[600],
    color: theme.palette.getContrastText(red[600]),
    marginRight: 2,
    borderWidth: 0,
  },
  eventAvatar: {
    width: theme.spacing(3),
    height: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    color: green[600],
    marginRight: 2,
    borderWidth: 0,
    fontSize: 20,
  },
  pdfAvatar: {
    width: theme.spacing(3),
    height: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.getContrastText(theme.palette.background.paper),
    marginRight: 2,
    borderWidth: 0,
    fontSize: 20,
    border: 0
  },
  statusIcon: {
    fontSize: 16,
  },
  progress: {
    padding: theme.spacing(2),
    marginLeft: "auto",
    marginRight: "auto",
    width: 72,
    height: 72,
  },
  loading: {
    textAlign: "center",
    height: 10,
    width: 10,
    position: "absolute",
    bottom: 500,
  },
  partnerIcon: {
    fontSize: 20,
    marginBottom: -5,
    color: teal[600],
  },
  locationIcon: {
    fontSize: 20,
    marginBottom: -5,
    color: deepOrange[600],
  },
  cargos: {
    marginBottom: theme.spacing(2),
  },
  cargoIcon: {
    fontSize: 20,
    marginBottom: -5,
    color: blue[600],
  },
  parentCargo: {
  },
  childCargoVisible: {
    marginLeft: 22,
    maxHeight: 100,
    overflow: 'hidden',
    display: 'block',
    transition: 'max-height 0.3s cubic-bezier(0.4,0,0.2,1)',
  },
  childCargoHidden: {
    marginLeft: 22,
    maxHeight: 0,
    overflow: 'hidden',
    display: 'block',
    transition: 'max-height 0.3s cubic-bezier(0.4,0,0.2,1)',
  },
  locationVisible: {
    display: 'block',
    maxHeight: 500,
    overflow: 'hidden',
    transition: 'max-height 0.3s cubic-bezier(0.4,0,0.2,1)',
  },
  locationHidden: {
    display: 'block',
    maxHeight: 0,
    overflow: 'hidden',
    transition: 'max-height 0.3s cubic-bezier(0.4,0,0.2,1)',
  },
  locationLine: {
    display: 'flex',
    alignItems: 'top',
  },
  locationPartner: {
    display: 'flex',
  },
  locationAddress: {
    display: 'flex',
  },
  eventsVisible: {
    marginTop: theme.spacing(2),
    display: 'block',
    maxHeight: 500,
    overflow: 'hidden',
    transition: 'max-height 0.3s cubic-bezier(0.4,0,0.2,1)',
  },
  eventsHidden: {
    marginTop: theme.spacing(2),
    display: 'block',
    maxHeight: 0,
    overflow: 'hidden',
    transition: 'max-height 0.3s cubic-bezier(0.4,0,0.2,1)',
  },
  eventLine: {
    display: 'flex',
    alignItems: 'top',
  },
  date: {
    whiteSpace: 'nowrap',
  },
  planDateIcon: {
    fontSize: 20,
    marginBottom: -5,
    color: grey[400],
  },
  factDateIcon: {
    fontSize: 20,
    marginBottom: -5,
    color: green[600],
  },
  locationDividerIcon: {
    fontSize: 20,
    marginBottom: -5,
    color: grey[400],
    transform: "rotate(90deg)",
  },
  locationProgressDividerIcon: {
    fontSize: 20,
    marginBottom: -5,
    color: blue[600],
    transform: "rotate(90deg)",
  },
  locationCompletedDividerIcon: {
    fontSize: 20,
    marginBottom: -5,
    color: green[600],
    transform: "rotate(90deg)",
  },
  eventIcon: {
    fontSize: 20,
    marginBottom: -5,
    marginRight: 4,
    color: green[600],
  },
  pdfLinks: {
    marginTop: theme.spacing(1),
    display: 'flex',
    flexWrap: 'wrap',
  },
  pdfLink: {
    textDecoration: 'none',
    color: theme.palette.text.primary,
    marginTop: 10,
    marginRight: 10,
    display: 'flex',
    whiteSpace: 'nowrap',
  },
  pdfIcon: {
    fontSize: 20,
    marginBottom: -5,
    // color: green[600],
  },
  cardContentExpanded: {
    paddingTop: 0,
    paddingBottom: 0,
    display: 'block',
    maxHeight: 1000,
    overflow: 'hidden',
    transition: 'max-height 0.3s cubic-bezier(0.4,0,0.2,1), padding-bottom 0.3s cubic-bezier(0.4,0,0.2,1)',
  },
  cardContentCollapsed: {
    paddingTop: 0,
    paddingBottom: 0,
    display: 'block',
    maxHeight: 0,
    overflow: 'hidden',
    transition: 'max-height 0.3s cubic-bezier(0.4,0,0.2,1), padding-bottom 0.3s cubic-bezier(0.4,0,0.2,1)',
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  toggleDetailsIcon: {
    marginTop: 0,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
    width: 40,
    height: 40,
    transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  toggleDetailsIconRotated: {
    transform: 'rotate(180deg)',
    transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
  },
  cardActionsVisible: {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 4,
    display: 'block',
    maxHeight: 100,
    overflow: 'hidden',
    transition: 'max-height 0.3s cubic-bezier(0.4,0,0.2,1)',
  },
  cardActionsHidden: {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 4,
    display: 'block',
    maxHeight: 0,
    overflow: 'hidden',
    transition: 'max-height 0.3s cubic-bezier(0.4,0,0.2,1)',
  },
}));

const formatDateTime = (dateTime: string) =>
{
  const date = new Date(dateTime);
  let result = date.toLocaleString(undefined, { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  return result.replace(/(\d{2})\.\s(\d{2})\.\s(\d{2}:\d{2})/g, "$1.$2 $3");
}

const formatDate = (date: string) =>
{
  const d = new Date(date);
  let result = d.toLocaleString(undefined, { month: "2-digit", day: "2-digit" });
  return result.replace(/(\d{2})\.\s(\d{2})\.\s?/g, "$1.$2");
}

interface Order extends ListOrderItem
{
}

const OrderCard = React.memo(({
  order,
  index,
  isExpanded,
  showDetails,
  lastSeenOrderId,
  lastSeenOrderEventId,
  lastSeenOrderAttachmentId,
  lastSeenInvoiceAttachmentId,
  onExpandClick,
  onDetailsClick,
  selectedOrderId,
  selectedOrderEventId
}: {
  order: Order;
  index: number;
  isExpanded: boolean;
  showDetails: boolean;
  lastSeenOrderId: number;
  lastSeenOrderEventId: number;
  lastSeenOrderAttachmentId: number;
  lastSeenInvoiceAttachmentId: number;
  onExpandClick: (orderId: number) => void;
  onDetailsClick: (orderId: number) => void;
  selectedOrderId?: number | null;
  selectedOrderEventId?: number | null;
}) =>
{
  const classes = useStyles();

  const handleExpandClick = React.useCallback(() =>
  {
    onExpandClick(order.OrderId);
  }, [order.OrderId, onExpandClick]);

  const handleDetailsClick = React.useCallback(() =>
  {
    onDetailsClick(order.OrderId);
  }, [order.OrderId, onDetailsClick]);

  const count = React.useCallback(<T,>(array: T[], predicate: (value: T) => boolean): number =>
  {
    let count = 0;
    array.forEach((item) => { if(predicate(item)) count++ });
    return count;
  }, []);

  const getLocationDividerClass = React.useCallback((o: Order, a: OrderLocation[], i: number, l: OrderLocation): string | undefined =>
  {
    if(!showDetails && i === 0)
    {
      const lastLocation = a[a.length - 1];
      if(lastLocation.UnloadingCompleted) return classes.locationCompletedDividerIcon;
      if(o.Locations.some(l => l.LoadingCompleted || l.UnloadingCompleted)) return classes.locationProgressDividerIcon;
    }
    if(a.slice(i + 1).some(ra => ra.UnloadingCompleted || ra.LoadingCompleted)) return classes.locationCompletedDividerIcon;
    if(l.LoadingCompleted) return classes.locationProgressDividerIcon;
    return classes.locationDividerIcon;
  }, [classes, showDetails]);

  return (
    <Card className={isExpanded ? (index > 0 ? classes.cardExpandedTopMargin : classes.cardExpanded) : classes.cardCollapsed} variant={isExpanded ? 'elevation' : 'outlined'} >
      <CardHeader
        data-orderid={order.OrderId}
        onClick={handleExpandClick}
        className={isExpanded ? classes.cardHeaderExpanded : classes.cardHeaderCollapsed}
        title={order.ExternalNumbers.length === 0 ? order.OrderNumber : order.ExternalNumbers.map(en => en.ExternalNumber).join(', ')}
        subheader={
          <>
            {order.ExternalNumbers.length > 0 ? order.OrderNumber : null}
            <div className={classes.cardStatusGroup}>
              <Avatar className={order.IsDelivered ? classes.completedAvatar : order.IsShipping ? classes.inProgressAvatar : classes.statusAvatar}>
                <DeliveryIcon className={classes.statusIcon} />
              </Avatar>
              <Avatar className={order.IsInvoiced ? classes.completedAvatar : order.IsInvoicing ? classes.inProgressAvatar : classes.statusAvatar}>
                <InvoiceIcon className={classes.statusIcon} />
              </Avatar>
              <Avatar className={order.PaymentDue ? classes.errorAvatar : order.IsPaid ? classes.completedAvatar : order.IsPaying ? classes.inProgressAvatar : classes.statusAvatar}>
                <PaymentIcon className={classes.statusIcon} />
              </Avatar>
              {order.Events.length > 0 ?
                <Badge className={classes.newOrderBadge} badgeContent={count(order.Events, e => e.OrderEventId > lastSeenOrderEventId)}>
                  <Avatar className={classes.eventAvatar} variant="circular">
                    <EventIcon fontSize="inherit" />
                  </Avatar>
                </Badge>
                : null}
              {order.PDFs.length > 0 ?
                <Badge className={classes.newOrderBadge} badgeContent={count(order.PDFs, p => (p.OrderAttachmentId ?? 0) > lastSeenOrderAttachmentId || (p.InvoiceAttachmentId ?? 0) > lastSeenInvoiceAttachmentId)} color="secondary">
                  <Avatar className={classes.pdfAvatar} variant="circular">
                    <PdfIcon fontSize="inherit" />
                  </Avatar>
                </Badge>
                : null}
            </div>
          </>
        }
        avatar={
          <Badge
            className={selectedOrderId === order.OrderId ? `${classes.newOrderBadge} ${classes.selectedOrderBadge}` : classes.newOrderBadge}
            badgeContent={selectedOrderId === order.OrderId ? (
              <NotificationsActiveOutlinedIcon fontSize="small" style={{ width: 18, height: 18 }} />
            ) : (
              " "
            )}
            variant={selectedOrderId === order.OrderId ? undefined : "dot"}
            invisible={order.OrderId <= lastSeenOrderId && selectedOrderId !== order.OrderId}
          >
            <Avatar className={order.PaymentDue ? classes.orderAvatarError : order.IsCompleted ? classes.orderAvatarCompleted : order.IsInProgress ? classes.orderAvatarInProgress : classes.orderAvatar}>
              <DescriptionOutlinedIcon />
            </Avatar>
          </Badge>
        }
        action={
          <IconButton size="small" className={isExpanded ? classes.cardActionButtonExpanded : classes.cardActionButtonCollapsed}>
            <span className={isExpanded ? `${classes.toggleDetailsIcon} ${classes.toggleDetailsIconRotated}` : classes.toggleDetailsIcon}>
              <ExpandMoreIcon fontSize="inherit" />
            </span>
          </IconButton>
        }
      />
      <CardContent
        data-orderid={order.OrderId}
        onClick={handleDetailsClick}
        className={isExpanded ? classes.cardContentExpanded : classes.cardContentCollapsed}
      >
        <>
          <div key={`${order.OrderId}c`} className={classes.cargos}>
            {order.Cargos?.map((c, i) => ({ c, i })).map(c =>
              <div key={`${order.OrderId}c${c.i}`} className={c.c.IsChild ? (c.i === 0 || showDetails ? classes.childCargoVisible : classes.childCargoHidden) : classes.parentCargo}>
                <CargoIcon className={classes.cargoIcon} /> {c.c.Name}: {c.c.Quantity} {c.c.QuantityUnit}{c.c.Weight ? `, ${c.c.Weight} kg` : null}{c.c.Volume ? `, ${c.c.Volume} m³` : null}
              </div>
            )}
          </div>
          {order.Locations?.map((l, i, a) => (
            <div key={`${order.OrderId}l${i}`} className={i === 0 || i === (order.Locations?.length - 1) || showDetails ? classes.locationVisible : classes.locationHidden}>
              <div className={classes.locationLine}>
                <div>
                  {l.UnloadingDate ? <div className={classes.date}>{l.UnloadingCompleted ? <FactDateIcon className={classes.factDateIcon} /> : <PlanDateIcon className={classes.planDateIcon} />} {formatDate(l.UnloadingDate)}</div> : null}
                  {l.LoadingDate ? <div className={classes.date}>{l.LoadingCompleted ? <FactDateIcon className={classes.factDateIcon} /> : <PlanDateIcon className={classes.planDateIcon} />} {formatDate(l.LoadingDate)}</div> : null}
                </div>
                <div>
                  {l.Partner ? <div className={classes.locationPartner}><PartnerIcon className={classes.partnerIcon} /> <div>{l.Partner.Name} </div></div> : null}
                  <div className={classes.locationAddress}>
                    <LocationIcon className={classes.locationIcon} /> <div>{l.Address.CountryCode2}{l.Address.Postcode ? '-' : null}{l.Address.Postcode} {l.Address.Settlement}{l.Address.Street ? ', ' : null}{l.Address.Street} {l.Address.StreetNumber}</div>
                  </div>
                </div>
              </div>
              {i < a.length - 1 ? <div ><LocationDividerIcon className={getLocationDividerClass(order, a, i, l)} /></div> : null}
            </div>
          ))}
          {order.Events?.length > 0 ? (
            <div key={`${order.OrderId}e`} className={showDetails ? classes.eventsVisible : classes.eventsHidden}>
              {order.Events?.map((e, i) =>
                <div key={`${order.OrderId}e${i}`} className={classes.eventLine} >
                  <Badge
                    className={selectedOrderEventId === e.OrderEventId ? classes.selectedOrderEventBadge : classes.newOrderEventBadge}
                    badgeContent={" "}
                    variant="dot"
                    invisible={e.OrderEventId <= lastSeenOrderEventId ? selectedOrderEventId !== e.OrderEventId : false}
                    overlap="circular"
                  >
                    <EventIcon className={classes.eventIcon} />
                  </Badge>
                  <div>{formatDateTime(e.EventTime)}: {e.Type}{e.Location ? ', ' : null}{e.Location}</div>
                </div>
              )}
            </div>
          ) : null}
          {order.PDFs.length > 0 ? (
            <div key={`${order.OrderId}p`} className={classes.pdfLinks}>
              {order.PDFs?.map((p, i) =>
                <a key={`${order.OrderId}p${i}`} className={classes.pdfLink} href={p.Url} target="_blank" rel="noopener noreferrer">
                  <Badge className={classes.newOrderBadge} badgeContent=" " variant="dot" invisible={(p.OrderAttachmentId !== undefined && p.OrderAttachmentId! <= lastSeenOrderAttachmentId) && (p.InvoiceAttachmentId !== undefined && p.InvoiceAttachmentId! <= lastSeenInvoiceAttachmentId)} overlap="circular">
                    <PdfIcon className={classes.pdfIcon} />
                  </Badge>
                  {p.Type}
                </a>
              )}
            </div>
          ) : null}
        </>
      </CardContent >
      {(order.Cargos?.length > 1 || order.Locations?.length > 2 || order.Events?.length > 0) ? (
        <CardActions className={isExpanded ? classes.cardActionsVisible : classes.cardActionsHidden}>
          <IconButton
            size="small"
            data-orderid={order.OrderId}
            onClick={handleDetailsClick}
            className={classes.toggleDetailsIcon}
          >
            <span className={showDetails ? `${classes.toggleDetailsIcon} ${classes.toggleDetailsIconRotated}` : classes.toggleDetailsIcon}>
              <ExpandMoreIcon fontSize="inherit" />
            </span>
          </IconButton>
        </CardActions>
      ) : null}
    </Card>
  );
});

OrderCard.displayName = 'OrderCard';
let prevBarcode = "";

export default function PartnerPortalIndex()
{
  const { t } = useTranslation();
  const classes = useStyles();
  const [skip, setSkip] = useState<number>(0);
  const [search, setSearch] = useState<string | null>(null);
  const { data, loading, error } = useListOrdersQuery({ variables: { Skip: skip, Search: search } });
  const [orders, setOrders] = useState<Order[]>([]);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [numberOfColumns, setNumberOfColumns] = React.useState(1);
  const [expandAll, setExpandAll] = useState(true);
  const searchField = useInput();
  const [noMoreResults, setNoMoreResults] = useState(false);
  const [hideTopBar, setHideTopBar] = useState(false);
  const lastScrollTop = useRef(0);
  const [lastSeenOrderId, setLastSeenOrderId] = useState(() =>
  {
    const stored = localStorage.getItem("lastSeenOrderId");
    return stored ? parseInt(stored, 10) : 0;
  });
  const [lastSeenOrderEventId, setLastSeenOrderEventId] = useState(() =>
  {
    const stored = localStorage.getItem("lastSeenOrderEventId");
    return stored ? parseInt(stored, 10) : 0;
  });
  const [lastSeenOrderAttachmentId, setLastSeenOrderAttachmentId] = useState(() =>
  {
    const stored = localStorage.getItem("lastSeenOrderAttachmentId");
    return stored ? parseInt(stored, 10) : 0;
  });
  const [lastSeenInvoiceAttachmentId, setLastSeenInvoiceAttachmentId] = useState(() =>
  {
    const stored = localStorage.getItem("lastSeenInvoiceAttachmentId");
    return stored ? parseInt(stored, 10) : 0;
  });
  const [expandedMap, setExpandedMap] = useState<{ [id: number]: boolean }>({});
  const [overrideExpandAll, setOverrideExpandAll] = useState<{ [id: number]: boolean }>({});
  const [detailsMap, setDetailsMap] = useState<{ [id: number]: boolean }>({});

  const [saveToken] = useSaveTokenMutation();
  const [logout] = useLogoutMutation();
  const history = useHistory();
  const urlQuery = useUrlQuery<{
    orderId: number | null;
    orderNumber: string | null;
    orderEventId: number | null;
  }>({ orderId: null, orderNumber: null, orderEventId: null });

  const handleExpandClick = React.useCallback((orderId: number) =>
  {
    setExpandedMap(prev =>
    {
      const currentValue = prev[orderId] ?? expandAll;
      return { ...prev, [orderId]: !currentValue };
    });
    setOverrideExpandAll(prev => ({ ...prev, [orderId]: true }));
  }, [expandAll]);

  const handleDetailsClick = React.useCallback((orderId: number) =>
  {
    setDetailsMap(prev => ({ ...prev, [orderId]: !prev[orderId] }));
  }, []);

  // Set detailsMap default for selected order if orderId and orderEventId are present in URL
  useEffect(() =>
  {
    if(typeof urlQuery.orderId === 'number' && urlQuery.orderEventId != null)
    {
      const orderIdNum = Number(urlQuery.orderId);
      setDetailsMap(prev =>
      {
        if(prev[orderIdNum] !== true)
        {
          return { ...prev, [orderIdNum]: true };
        }
        return prev;
      });
    }
  }, [urlQuery.orderId, urlQuery.orderEventId]);

  useEffect(() =>
  {
    (async () =>
    {
      if(data?.ListOrders)
      {
        if(!data.ListOrders.IsUser)
        {
          if(data.ListOrders.UpdatedToken)
          {
            await saveToken({ variables: { token: data.ListOrders.UpdatedToken } });
          }
          else
          {
            await logout();
            if(data.ListOrders.InvitationCode)
            {
              history.replace(`/invitation/${data.ListOrders.InvitationCode}`);
            }
            else
            {
              window.location.reload();
            }
            return;
          }
        }
        if(data.ListOrders.Orders.length === 0)
        {
          setNoMoreResults(true);
          return
        };
        if(orders.length > 0 && orders[orders.length - 1].OrderId <= data.ListOrders.Orders[0].OrderId) return;

        const newOrders = data.ListOrders.Orders.map(o => ({ ...o }));
        setOrders([...orders, ...newOrders]);

        checkAndSaveLastSeenValues(data);
      }
    })();
    // eslint-disable-next-line
  }, [data]);

  const checkAndSaveLastSeenValues = (data: ListOrdersQuery) =>
  {
    const maxOrderId = data.ListOrders.Orders.reduce((acc, o) => Math.max(acc, o.OrderId), lastSeenOrderId);
    if(maxOrderId > lastSeenOrderId)
    {
      localStorage.setItem("lastSeenOrderId", maxOrderId.toString());
      if(lastSeenOrderId === 0) setLastSeenOrderId(maxOrderId);
    }

    const maxOrderEventId = data.ListOrders.Orders.reduce((acc, o) => Math.max(acc, o.Events.length > 0 ? o.Events.reduce((acc, e) => Math.max(acc, e.OrderEventId), lastSeenOrderEventId) : 0), lastSeenOrderEventId);
    if(maxOrderEventId > lastSeenOrderEventId)
    {
      localStorage.setItem("lastSeenOrderEventId", maxOrderEventId.toString());
      if(lastSeenOrderEventId === 0) setLastSeenOrderEventId(maxOrderEventId);
    }

    const maxOrderAttachmentId = data.ListOrders.Orders.reduce((acc, o) => Math.max(acc, o.PDFs.length > 0 ? o.PDFs.reduce((acc, e) => Math.max(acc, e.OrderAttachmentId ?? 0), lastSeenOrderAttachmentId) : 0), lastSeenOrderAttachmentId);
    if(maxOrderAttachmentId > lastSeenOrderAttachmentId)
    {
      localStorage.setItem("lastSeenOrderAttachmentId", maxOrderAttachmentId.toString());
      if(lastSeenOrderAttachmentId === 0) setLastSeenOrderAttachmentId(maxOrderAttachmentId);
    }

    const maxInvoiceAttachmentId = data.ListOrders.Orders.reduce((acc, o) => Math.max(acc, o.PDFs.length > 0 ? o.PDFs.reduce((acc, e) => Math.max(acc, e.InvoiceAttachmentId ?? 0), lastSeenInvoiceAttachmentId) : 0), lastSeenInvoiceAttachmentId);
    if(maxInvoiceAttachmentId > lastSeenInvoiceAttachmentId)
    {
      localStorage.setItem("lastSeenInvoiceAttachmentId", maxInvoiceAttachmentId.toString());
      if(lastSeenInvoiceAttachmentId === 0) setLastSeenInvoiceAttachmentId(maxInvoiceAttachmentId);
    }
  }

  const sentinelRefCallback = React.useCallback((node: HTMLDivElement | null) =>
  {
    if(!node) return;
    const observer = new IntersectionObserver(([entry]) =>
    {
      if(entry.isIntersecting)
      {
        setSkip(orders.length);
      }
    });
    observer.observe(node);

    return () => observer.disconnect();
  }, [orders.length]);

  const toggleExpandAll = React.useCallback(() =>
  {
    setExpandAll(prevExpandAll =>
    {
      setExpandedMap(prev =>
      {
        const updated = { ...prev };
        orders.forEach(o => { updated[o.OrderId] = !prevExpandAll; });
        return updated;
      });
      setOverrideExpandAll({});  // Reset overrides when global setting changes
      return !prevExpandAll;
    });
  }, [orders]);

  const searchButtonClick = React.useCallback(() =>
  {
    setOrders([]);
    setSkip(0);
    setSearch(searchField.props.value);
    setExpandAll(true);
  }, [setOrders, setSkip, setSearch, setExpandAll, searchField.props.value]);

  const clearSearchButtonClick = React.useCallback(() =>
  {
    setOrders([]);
    setSkip(0);
    setSearch(null);
    setExpandAll(true);
    searchField.setValue('');
  }, [setOrders, setSkip, setSearch, setExpandAll, searchField]);

  const showBarcodeDialog = React.useCallback(() =>
  {
    setDialogOpen(true);
  }, []);

  useEffect(() =>
  {
    const handleScroll = () =>
    {
      const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
      if(currentScrollTop > lastScrollTop.current)
      {
        // Scrolling down
        setHideTopBar(true);
      } else
      {
        // Scrolling up
        setHideTopBar(false);
      }
      lastScrollTop.current = currentScrollTop <= 0 ? 0 : currentScrollTop;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  React.useEffect(() =>
  {
    function recalcColumns()
    {
      const containerWidth = containerRef.current?.offsetWidth || 400;
      // at least one column per 400px:
      const possibleColumns = Math.max(Math.floor(containerWidth / 400), 1);
      setNumberOfColumns(possibleColumns);
    }

    let timeoutId: ReturnType<typeof setTimeout>;
    function debouncedRecalcColumns()
    {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => recalcColumns(), 200);
    }

    window.addEventListener("resize", debouncedRecalcColumns);
    recalcColumns();
    return () => window.removeEventListener("resize", debouncedRecalcColumns);
  }, []);

  const columns = React.useMemo(() =>
  {
    const result: Order[][] = Array.from({ length: numberOfColumns }, () => []);
    orders.forEach((o, i) =>
    {
      result[i % numberOfColumns].push(o);
    });
    return result;
  }, [orders, numberOfColumns]);

  const [dialogOpen, setDialogOpen] = useState(false);

  const handleBarcodeUpdate = useCallback((err: unknown, result?: any) =>
  {
    if(result)
    {
      const barcode = result.getText();
      if(barcode !== prevBarcode)
      {
        prevBarcode = barcode;
        const osc = new Tone.Oscillator(880, "square").toDestination();
        osc.onstop = s =>
        {
          Tone.Transport.stop();
          searchField.setValue(barcode);
          searchButtonClick();
          setSearch(barcode);
          setDialogOpen(false);
          setTimeout(() => prevBarcode = '', 2000);
          setDialogOpen(false);
        };
        osc.sync().start(0).stop(0.05);
        Tone.Transport.start();
      }
    }
  }, [searchField, searchButtonClick, setDialogOpen]);

  const [didHandleOrderId, setDidHandleOrderId] = useState(false);

  useEffect(() =>
  {
    if(didHandleOrderId) return;
    if(urlQuery.orderId)
    {
      const orderIdNum = Number(urlQuery.orderId);
      if(orders.length === 0) return;
      const found = orders.some(o => o.OrderId === orderIdNum);
      if(found)
      {
        const ref = orderRefs.current[orderIdNum];
        if(ref) ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setDidHandleOrderId(true);
      } else if(urlQuery.orderNumber && searchField.props.value !== urlQuery.orderNumber)
      {
        setOrders([]);
        setSkip(0);
        searchField.setValue(urlQuery.orderNumber);
        setSearch(urlQuery.orderNumber);
        setExpandAll(true);
        setDidHandleOrderId(true);
      }
    }
    // eslint-disable-next-line
  }, [urlQuery.orderId, urlQuery.orderNumber, orders, didHandleOrderId]);

  const orderRefs = React.useRef<{ [orderId: number]: HTMLDivElement | null }>({});

  if(error) return <Typography>{error.message}</Typography>;

  return (
    <div className={classes.root}>
      <BarcodeDialog
        dialogOpen={dialogOpen}
        onBarcodeUpdate={handleBarcodeUpdate}
        onCancel={() => { setDialogOpen(false) }}
      />
      <div className={`${classes.topBar} ${hideTopBar ? classes.topBarHidden : ''}`}>
        <Paper component="form" className={classes.searchBox} onSubmit={(e) => { e.preventDefault(); searchButtonClick(); }}>
          <InputBase type="search" {...searchField.props} className={classes.searchInput} inputProps={{ style: { width: numberOfColumns > 1 ? (search ? 400 - 26 : 400) : search ? 'calc(100vw - 168px - 26px)' : 'calc(100vw - 168px)' } }} />
          <IconButton disabled={!searchField.props.value || search === searchField.props.value} type="submit" className={classes.searchButton} aria-label="search" onClick={searchButtonClick}>
            <SearchIcon />
          </IconButton>
          {search ? <>
            <Divider className={classes.divider} orientation="vertical" />
            <IconButton size="small" className={classes.searchButton} aria-label="directions" onClick={clearSearchButtonClick}>
              <ClearOutlinedIcon fontSize="inherit" />
            </IconButton>
          </>
            : null}
        </Paper>
        <div className={classes.filters} >
          <IconButton size="small" className={classes.readBarcodeButton} onClick={showBarcodeDialog}>
            <SvgIcon className={classes.barcodeIcon}>
              <path stroke="null" id="svg_3" d="m1.046054,4.697369c0,-0.576125 0.467108,-1.043233 1.043233,-1.043233l2.608082,0l0,-1.043233l-2.608082,0c-1.152251,0 -2.086466,0.934215 -2.086466,2.086466l0,2.608082l1.043233,0l0,-2.608082zm0,14.605261l0,-2.608082l-1.043233,0l0,2.608082c0,1.152251 0.934215,2.086466 2.086466,2.086466l2.608082,0l0,-1.043233l-2.608082,0c-0.576125,0 -1.043233,-0.467108 -1.043233,-1.043233zm21.907892,0c0,0.576125 -0.467108,1.043233 1.043233,1.043233l-2.608082,0l0,1.043233l2.608082,0c1.152251,0 2.086466,-0.934215 2.086466,-2.086466l0,-2.608082l-1.043233,0l0,2.608082zm-1.043233,-16.691727l-2.608082,0l0,1.043233l2.608082,0c0.576125,0 1.043233,0.467108 1.043233,1.043233l0,2.608082l1.043233,0l0,-2.608082c0,-1.152251 -0.934215,-2.086466 -2.086466,-2.086466zm-20.864659,9.389096c0,0.287932 0.233684,0.521616 0.521616,0.521616l20.864659,0c0.287932,0 0.521616,-0.233684 0.521616,-0.521616s-0.233684,-0.521616 -0.521616,-0.521616l-20.864659,0c-0.287932,0 -0.521616,0.233684 -0.521616,0.521616zm8.345864,-6.259398l-1.043233,0l0,4.694548l1.043233,0l0,-4.694548zm-6.259398,0l0,4.694548l1.043233,0l0,-4.694548l-1.043233,0zm8.345864,0l-1.043233,0l0,4.694548l1.043233,0l0,-4.694548zm5.216165,0l-1.043233,0l0,4.694548l1.043233,0l0,-4.694548zm2.086466,0l-1.043233,0l0,4.694548l1.043233,0l0,-4.694548zm1.043233,0l0,4.694548l1.043233,0l0,-4.694548l-1.043233,0zm-12.518795,0l-2.086466,0l0,4.694548l2.086466,0l0,-4.694548zm7.302631,0l-2.086466,0l0,4.694548l2.086466,0l0,-4.694548zm-6.259398,12.518795l1.043233,0l0,-4.694548l-1.043233,0l0,4.694548zm-4.172932,0l0,-4.694548l-1.043233,0l0,4.694548l1.043233,0zm6.259398,0l1.043233,0l0,-4.694548l-1.043233,0l0,4.694548zm5.216165,0l1.043233,0l0,-4.694548l-1.043233,0l0,4.694548zm2.086466,0l1.043233,0l0,-4.694548l-1.043233,0l0,4.694548zm3.129699,0l0,-4.694548l-1.043233,0l0,4.694548l1.043233,0zm-15.648494,0l2.086466,0l0,-4.694548l-2.086466,0l0,4.694548zm7.302631,0l2.086466,0l0,-4.694548l-2.086466,0l0,4.694548z" clip-rule="evenodd" fill-rule="evenodd" />
            </SvgIcon></IconButton>
          <IconButton size="small" className={classes.expandAllToggleButton} onClick={toggleExpandAll} >{expandAll ? <ExpandLessIcon fontSize="inherit" /> : <ExpandMoreIcon fontSize="inherit" />}</IconButton>
        </div>
      </div>
      <div ref={containerRef} className={classes.pinterestColumns}>
        {columns.map((col, colIndex) => (
          <div key={`col-${colIndex}`} className={classes.pinterestColumn} style={{ width: `${100 / numberOfColumns}%` }}>
            {col.map((order, i) => (
              <div
                key={order.OrderId}
                ref={el => { orderRefs.current[order.OrderId] = el; }}
              >
                <OrderCard
                  order={order}
                  index={i}
                  isExpanded={overrideExpandAll[order.OrderId] ? expandedMap[order.OrderId] : (expandedMap[order.OrderId] ?? expandAll)}
                  showDetails={detailsMap[order.OrderId] ?? false}
                  selectedOrderId={urlQuery.orderId}
                  selectedOrderEventId={urlQuery.orderEventId}
                  lastSeenOrderId={lastSeenOrderId}
                  lastSeenOrderEventId={lastSeenOrderEventId}
                  lastSeenOrderAttachmentId={lastSeenOrderAttachmentId}
                  lastSeenInvoiceAttachmentId={lastSeenInvoiceAttachmentId}
                  onExpandClick={handleExpandClick}
                  onDetailsClick={handleDetailsClick}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
      {!loading ? <div ref={sentinelRefCallback} className={classes.loading}></div> : null}
      {noMoreResults ?
        <div style={{ height: 23, display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 8 }}>{t('searchResult', { count: orders.length })}</div>
        :
        <div className={classes.progress}>
          {loading ? <CircularProgress color="secondary" /> : null}
        </div>
      }
    </div>
  );
}
